// @flow

import React from 'react';

import { useTrackingContext } from '@zapier/toolbox-tracking';
import { extractParams } from '@zapier/url-utils';

import { useCurrentAccountId } from '@zapier/payment/src/hooks/useCurrentAccountId';
import { useCurrencyRuleset } from '@zapier/payment/src/hooks/useCurrencyRuleset';
import {
  type Currency,
  defaultCurrency,
} from '@zapier/payment/src/hooks/useCurrencies';
import { paymentSourceMethods } from '@zapier/payment/src/types';
import usePaymentSourceState from './hooks/usePaymentSourceState';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { Skeleton } from './Skeleton';
import { apiKeys } from './apiKeys';

type Props = {
  autoEdit?: boolean,
  editInModal?: boolean,
  onDone?: () => void,
  // $FlowFixMe
  onSaveSuccess?: () => void,
  selectedCurrency?: Currency,
};

export const PaymentApp = ({
  autoEdit,
  editInModal,
  onDone,
  onSaveSuccess,
  selectedCurrency = defaultCurrency,
}: Props) => {
  const { trackEvent } = useTrackingContext();
  const { accountId } = useCurrentAccountId();
  const startEdit = Boolean(
    extractParams(window.location.href)?.section ===
      paymentSourceMethods.card || autoEdit
  );

  const {
    currentSource,
    error,
    forceCardUpdate,
    isEditing,
    isLoading: isLoadingPaymentSource,
    isSaving,
    method,
    onCancelEdit,
    onEdit,
    onError,
    onHideSuccessMessage,
    onSave,
    onSelectMethod,
    showSuccessMessage,
    supportedMethods,
  } = usePaymentSourceState({
    accountId,
    onDone,
    // $FlowFixMe
    onSaveSuccess,
    onTrack: trackEvent,
    startEdit,
  });
  const {
    currencyRuleset,
    isLoading: isLoadingCurrencyRuleset,
  } = useCurrencyRuleset();

  const isLoading = isLoadingPaymentSource || isLoadingCurrencyRuleset;

  if (isLoading) {
    return <Skeleton />;
  }

  return (
    <PaymentMethodsSection
      apiKeys={{
        braintree: apiKeys.braintreePubKey,
        stripe: apiKeys.stripePubKey,
      }}
      currencyRuleset={currencyRuleset}
      currentMethod={method}
      editInModal={Boolean(editInModal)}
      error={error}
      forceCardUpdate={forceCardUpdate}
      hasPaymentSource={Boolean(currentSource)}
      isEditing={isEditing}
      isEditingInFlight={isSaving}
      onCancelEdit={onCancelEdit}
      onEdit={onEdit}
      onErrorEdit={onError}
      onHideSuccessMessage={onHideSuccessMessage}
      onSaveEdit={onSave}
      onSelectSource={onSelectMethod}
      savedSource={currentSource}
      selectedCurrency={selectedCurrency}
      showChangeSuccessMessage={showSuccessMessage}
      supportedMethods={supportedMethods}
    />
  );
};
